import React, { useEffect, useState, useRef, useMemo } from "react";
import cx from "classnames";

import { USD_DECIMALS, INCREASE, getLiquidationPrice } from "lib/legacy";
import { useChartPrices } from "domain/legacy";
import { Trans } from "@lingui/macro";
import ChartTokenSelector from "./ChartTokenSelector";
import { getTokenInfo } from "domain/tokens/utils";
import { formatAmount, numberWithCommas } from "lib/numbers";
import { getToken } from "config/tokens";
import TVChartContainer from "components/TVChartContainer/TVChartContainer";
import { t } from "@lingui/macro";
import { availableNetworksForChart } from "components/TVChartContainer/constants";
import { TVDataProvider } from "domain/tradingview/TVDataProvider";
import { getTokenBySymbol } from "config/tokens";
import { getConstant } from "config/chains";
import { useLocalStorageByChainId } from "lib/localStorage";
import { RxTriangleUp, RxTriangleDown } from "react-icons/rx";
import { useHourlyVolumeByToken } from "domain/stats";
import Tooltip from "components/Tooltip/Tooltip";
import ExternalLink from "components/ExternalLink/ExternalLink";

import { GRAY_THEME, DARK_THEME } from "./constants";

const PRICE_LINE_TEXT_WIDTH = 15;

export function getChartToken(swapOption, fromToken, toToken, chainId) {
  if (!fromToken || !toToken) {
    return;
  }

  return toToken;
}

export default function ExchangeTVChart(props) {
  const {
    swapOption,
    fromTokenAddress,
    toTokenAddress,
    infoTokens,
    chainId,
    positions,
    savedShouldShowPositionLines,
    orders,
    setToTokenAddress,
    theme,
    chartColor,
  } = props;
  const [currentSeries] = useState();
  const defaultCollateralSymbol = getConstant(chainId, "defaultCollateralSymbol");
  const [shortCollateralAddress] = useLocalStorageByChainId(
    chainId,
    "Short-Collateral-Address",
    getTokenBySymbol(chainId, defaultCollateralSymbol).address
  );

  const dayVolumeData = useHourlyVolumeByToken(toTokenAddress)
  const currentVolumeInfo = dayVolumeData.data?.volume;

  const shortCollateralToken = getTokenInfo(infoTokens, shortCollateralAddress);

  const dataProvider = useRef();

  const fromToken = getTokenInfo(infoTokens, fromTokenAddress);
  const toToken = getTokenInfo(infoTokens, toTokenAddress);

  const [chartToken, setChartToken] = useState({
    maxPrice: null,
    minPrice: null,
  });

  useEffect(() => {
    dataProvider.current = new TVDataProvider();
  }, []);

  useEffect(() => {
    const tmp = getChartToken(swapOption, fromToken, toToken, chainId);
    setChartToken(tmp);
  }, [swapOption, fromToken, toToken, chainId]);

  const currentOrders = useMemo(() => {
    if (!chartToken) {
      return [];
    }

    return orders
      .filter((order) => {
        const indexToken = getToken(chainId, order.indexToken);
        return order.indexToken === chartToken.address || (chartToken.isNative && indexToken.isWrapped);
      })
      .map((order) => {
        const indexToken = getToken(chainId, order.indexToken);
        const longOrShortText = order.isLong ? t`Long` : t`Short`;
        const orderTypeText = order.type === INCREASE ? t`Inc.` : t`Dec.`;
        let tokenSymbol;
        if (indexToken && indexToken.symbol) {
          tokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;
        }
        const title = `${orderTypeText} ${tokenSymbol} ${longOrShortText}`;
        return { title, price: parseFloat(formatAmount(order.triggerPrice, USD_DECIMALS, 2)) };
      });
  }, [orders, chartToken, chainId]);

  const currentPositions = useMemo(() => {
    if (!positions || !chartToken) {
      return [];
    }
    return positions
      .filter((p) => p.indexToken.address === chartToken.address)
      .map((position) => {
        const longOrShortText = position.isLong ? t`Long` : t`Short`;
        return {
          open: {
            price: parseFloat(formatAmount(position.averagePrice, USD_DECIMALS, 2)),
            title: t`Open ${position.indexToken.symbol} ${longOrShortText}`,
          },
          liquidation: {
            price: parseFloat(formatAmount(getLiquidationPrice(position), USD_DECIMALS, 2)),
            title: t`Liq. ${position.indexToken.symbol} ${longOrShortText}`,
          },
        };
      });
  }, [chartToken, positions]);

  const chartLines = useMemo(() => {
    const lines = [];
    if (currentOrders.length > 0) {
      lines.push(...currentOrders);
    }

    if (currentPositions.length > 0) {
      currentPositions.forEach((position) => {
        lines.push(position.open);
        lines.push(position.liquidation);
      });
    }

    return lines;
  }, [currentOrders, currentPositions]);

  const ref = useRef(null);

  const currentAveragePrice =
    chartToken.maxPrice && chartToken.minPrice ? chartToken.maxPrice.add(chartToken.minPrice).div(2) : null;
  const [priceData, updatePriceData] = useChartPrices(
    chainId,
    chartToken.symbol,
    chartToken.isStable,
    "1h",
    currentAveragePrice
  );

  useEffect(() => {
    const interval = setInterval(() => {
      updatePriceData(undefined, true);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [updatePriceData]);

  useEffect(() => {
    const lines = [];
    if (currentSeries && savedShouldShowPositionLines) {
      if (currentOrders && currentOrders.length > 0) {
        currentOrders.forEach((order) => {
          const indexToken = getToken(chainId, order.indexToken);
          let tokenSymbol;
          if (indexToken && indexToken.symbol) {
            tokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;
          }
          const title = `${order.type === INCREASE ? "Inc." : "Dec."} ${tokenSymbol} ${
            order.isLong ? "Long" : "Short"
          }`;
          const color = "#3a3e5e";
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(order.triggerPrice, USD_DECIMALS, 2)),
              color,
              title: title.padEnd(PRICE_LINE_TEXT_WIDTH, " "),
            })
          );
        });
      }
      if (currentPositions && currentPositions.length > 0) {
        const color = "#3a3e5e";

        positions.forEach((position) => {
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(position.averagePrice, USD_DECIMALS, 2)),
              color,
              title: `Open ${position.indexToken.symbol} ${position.isLong ? "Long" : "Short"}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ),
            })
          );

          const liquidationPrice = getLiquidationPrice(position);
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(liquidationPrice, USD_DECIMALS, 2)),
              color,
              title: `Liq. ${position.indexToken.symbol} ${position.isLong ? "Long" : "Short"}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ),
            })
          );
        });
      }
    }
    return () => {
      lines.forEach((line) => currentSeries.removePriceLine(line));
    };
  }, [currentOrders, currentSeries, chainId, savedShouldShowPositionLines, currentPositions, positions]);

  let high;
  let low;
  let deltaPrice;
  let delta;
  let deltaPercentage;
  let deltaPercentageStr;

  const now = parseInt(Date.now() / 1000);
  const timeThreshold = now - 24 * 60 * 60;

  if (priceData) {
    for (let i = priceData.length - 1; i > 0; i--) {
      const price = priceData[i];
      if (price.time < timeThreshold) {
        break;
      }
      if (!low) {
        low = price.low;
      }
      if (!high) {
        high = price.high;
      }

      if (price.high > high) {
        high = price.high;
      }
      if (price.low < low) {
        low = price.low;
      }

      deltaPrice = price.open;
    }
  }

  if (deltaPrice && currentAveragePrice) {
    const average = parseFloat(formatAmount(currentAveragePrice, USD_DECIMALS, 2));
    delta = average - deltaPrice;
    deltaPercentage = (delta * 100) / average;
    if (deltaPercentage === 0) {
      deltaPercentageStr = "0.00";
    } else {
      deltaPercentageStr = `$${Math.abs(delta).toFixed(2)} (${Math.abs(deltaPercentage).toFixed(2)}%)`;
    }
  }

  if (!chartToken) {
    return null;
  }

  const onSelectToken = (token) => {
    const tmp = getTokenInfo(infoTokens, token.address);
    setChartToken(tmp);
    setToTokenAddress(swapOption, token.address);
  };

  const toTokenInfo = getTokenInfo(infoTokens, toTokenAddress);

  const borrowFeeTextLong = formatAmount(toTokenInfo.fundingRate, 4, 4) + "% / 1h";
  const borrowFeeTextShort = formatAmount(shortCollateralToken.fundingRate, 4, 4) + "% / 1h";

  return (
    <div className="ExchangeChart tv" ref={ref}>
      <div
        className={cx("ExchangeChart-top", "App-box", "App-box-border", {
          gray: theme === GRAY_THEME,
          dark: theme === DARK_THEME,
        })}
      >
        <div className="ExchangeChart-top-inner">
          <ChartTokenSelector
            chainId={chainId}
            selectedToken={chartToken}
            swapOption={swapOption}
            infoTokens={infoTokens}
            onSelectToken={onSelectToken}
            className="chart-token-selector"
          />

          <div className="ExchangeChart-first-block">
            <div className="ExchangeChart-main-price">
              ${chartToken.maxPrice && formatAmount(chartToken.maxPrice, USD_DECIMALS, 2, true)}
            </div>
            <div className="ExchangeChart-info-label">
              ≈ {chartToken.minPrice && formatAmount(chartToken.minPrice, USD_DECIMALS, 2, true)} USDT
            </div>
          </div>

          <div className="ExchangeChart-second-block">
            <div className="ExchangeChart-info-label">24h Change</div>

            {deltaPercentage ? (
              <div
                className={cx("ExchangeChart-info-percentage", {
                  positive: deltaPercentage > 0,
                  negative: deltaPercentage < 0,
                })}
              >
                {!deltaPercentageStr && "-"}
                {deltaPercentage > 0 ? <RxTriangleUp /> : <RxTriangleDown />}
                {deltaPercentageStr && deltaPercentageStr}
              </div>
            ) : (
              <div className="skeleton"></div>
            )}
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label">24h High</div>
            {high ? (
              <div>
                {!high && "-"}
                {high && numberWithCommas(high.toFixed(2))}
              </div>
            ) : (
              <div className="skeleton"></div>
            )}
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label">24h Low</div>
            {low ? (
              <div>
                {!low && "-"}
                {low && numberWithCommas(low.toFixed(2))}
              </div>
            ) : (
              <div className="skeleton"></div>
            )}
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label">24h Volume</div>
            {currentVolumeInfo ? (
              <div>{formatAmount(currentVolumeInfo, USD_DECIMALS, 0, true)}</div>
            ) : (
              <div className="skeleton"></div>
            )}
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label">Borrow Fee (l)</div>

            {borrowFeeTextLong.includes("-") ? (
              <div className="skeleton"></div>
            ) : (
              <Tooltip
                handle={borrowFeeTextLong}
                position="center-bottom"
                renderContent={() => {
                  return (
                    <div>
                      <Trans>
                        The borrow fee is calculated as (assets borrowed) / (total assets in pool) * 0.01% per hour.
                        <br />
                        <br />
                        <ExternalLink href="">More Info</ExternalLink>
                      </Trans>
                    </div>
                  );
                }}
              ></Tooltip>
            )}
          </div>
          <div className="ExchangeChart-additional-info">
            <div className="ExchangeChart-info-label">Borrow Fee (s)</div>

            {borrowFeeTextShort.includes("-") ? (
              <div className="skeleton"></div>
            ) : (
              <Tooltip
                handle={borrowFeeTextShort}
                position="center-bottom"
                renderContent={() => {
                  return (
                    <div>
                      <Trans>
                        The borrow fee is calculated as (assets borrowed) / (total assets in pool) * 0.01% per hour.
                        <br />
                        <br />
                        <ExternalLink href="">More Info</ExternalLink>
                      </Trans>
                    </div>
                  );
                }}
              ></Tooltip>
            )}
          </div>
        </div>
      </div>
      <div className="ExchangeChart-bottom App-box App-box-border">
        {availableNetworksForChart.includes(chainId) && chartToken.symbol && chainId ? (
          <TVChartContainer
            chartLines={chartLines}
            savedShouldShowPositionLines={savedShouldShowPositionLines}
            symbol={chartToken.symbol}
            chainId={chainId}
            onSelectToken={onSelectToken}
            dataProvider={dataProvider.current}
            chartColor={chartColor}
          />
        ) : (
          <p className="ExchangeChart-error">Sorry, chart is not supported on this network yet.</p>
        )}
      </div>
    </div>
  );
}
